import React from 'react';
import { FluentProvider, makeStyles, tokens } from '@fluentui/react-components';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import useStore from './store';
import { Navbar } from './components/layout/Navbar';
import { MediaList } from './components/media/MediaList';
import { MediaPage } from './pages/Media';
import { VoiceActorPage } from './pages/VoiceActor';
import { Breadcrumbs } from './components/layout/Breadcrumbs';
import { lightTheme, darkTheme } from './theme';
import { useScrollManager } from './hooks/useScrollManager';

const useStyles = makeStyles({
    stickyHeader: {
        position: 'sticky',
        top: 0,
        zIndex: 100,
        backgroundColor: tokens.colorNeutralBackground1,
        boxShadow: tokens.shadow4,
    },
    container: {
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh'
    },
    mainContent: {
        paddingTop: 'var(--spacingVerticalXS)',
        paddingLeft: 'var(--spacingVerticalXXS)',
        paddingRight: 'var(--spacingVerticalXXS)',
        paddingBottom: 'var(--spacingVerticalM)',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--spacingVerticalXS)',
        '@media (min-width: 460px)': {
            paddingTop: 'var(--spacingVerticalS)',
            paddingLeft: 'var(--spacingVerticalS)',
            paddingRight: 'var(--spacingVerticalS)',
            gap: 'var(--spacingVerticalS)',
        },
        '@media (min-width: 768px)': {
            paddingLeft: 'var(--spacingVerticalXL)',
            paddingRight: 'var(--spacingVerticalXL)',
            gap: 'var(--spacingVerticalM)',
        },
    },
});

const App: React.FC = () => {
    const styles = useStyles();
    const {
        userMediaList,
        isDarkTheme,
        isLoading,
        fetchMediaLists,
        setIsDarkTheme,
        userName,
        setUserName,
        language,
        setLanguage,
        selectedMediaId,
    } = useStore();

    const handleThemeChange = (_ev: React.SyntheticEvent, data: { checked: boolean }) => {
        setIsDarkTheme(data.checked);
    };

    const selectedMedia = React.useMemo(() => {
        if (!selectedMediaId) return null;
        return userMediaList.find(entry => entry.id === selectedMediaId);
    }, [selectedMediaId, userMediaList]);

    if (isLoading) {
        return <div>Loading...</div>;
    }

    return (
        <FluentProvider
            theme={isDarkTheme ? darkTheme : lightTheme}
            style={{
                minHeight: '100vh',
                backgroundColor: isDarkTheme ? darkTheme.colorNeutralBackground1 : lightTheme.colorNeutralBackground1
            }}
        >
            <BrowserRouter>
                {/* Move useScrollManager inside a component within BrowserRouter */}
                <ScrollManagerProvider>
                    <div className={styles.container}>
                        <div className={styles.stickyHeader}>
                            <Navbar
                                isDarkTheme={isDarkTheme}
                                onThemeChange={handleThemeChange}
                                userName={userName}
                                onUserNameChange={setUserName}
                                language={language}
                                onLanguageChange={setLanguage}
                                onRefresh={fetchMediaLists}
                            />
                            <Breadcrumbs />
                        </div>
                        <Routes>
                            <Route path="/" element={
                                <div className={styles.mainContent}>
                                    <MediaList userMediaList={userMediaList} />
                                </div>
                            } />
                            <Route path="/media/:id" element={
                                selectedMedia ? (
                                    <MediaPage
                                        media={selectedMedia}
                                    />
                                ) : <Navigate to="/" />
                            } />
                            <Route path="/actor/:id" element={<VoiceActorPage />} />
                        </Routes>
                    </div>
                </ScrollManagerProvider>
            </BrowserRouter>
        </FluentProvider>
    );
};

// Create a wrapper component to use the scroll manager
const ScrollManagerProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    useScrollManager();
    return <>{children}</>;
};

export default App;